import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
  api,
  type IngestionOverview,
  type QueueItem,
  type SyncStatus,
} from "../../lib/api";
import { formatBytes, formatNumber, formatTime, timeAgo } from "../../lib/format";
import { Bar, Card, ErrorNote, PageHeader, Spinner, useToast } from "../../components/ui";
import { useAuth } from "../../lib/auth";

const CATEGORIES = [
  { value: "", label: "Infer from the document" },
  { value: "EMERGENCY", label: "Emergency" },
  { value: "STC", label: "STC" },
  { value: "TRAINING", label: "Training" },
  { value: "OT_SECURITY", label: "OT Security" },
  { value: "SCADA", label: "SCADA" },
  { value: "SITE_INFO", label: "Site info" },
];

// Stage label colours, from the prototype's ingestion queue.
const STAGE_COLOUR: Record<string, string> = {
  QUEUED: "#A49DA8",
  FETCHING: "#4B2E83",
  EXTRACTING: "#4B2E83",
  OCR: "#75650B",
  CHUNKING: "#4B2E83",
  EMBEDDING: "#2F6420",
  INDEXING: "#1D5F72",
  RENDERING: "#1D5F72",
  INDEXED: "#2F6420",
  FAILED: "#B23438",
  DEAD_LETTER: "#B23438",
  SKIPPED: "#A49DA8",
};

const STAGE_BAR: Record<string, string> = {
  OCR: "#ECF166",
  CHUNKING: "#AD96DC",
  EMBEDDING: "#8DE971",
  INDEXING: "#74D1EA",
  RENDERING: "#74D1EA",
  QUEUED: "#DDD7DA",
};

export function IngestionScreen() {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState("");
  const [dragging, setDragging] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ingestion"],
    queryFn: () => api.get<IngestionOverview>("/admin/ingestion"),
    // Poll while anything is in flight so the queue bars actually move; back
    // off when idle rather than hammering the API forever.
    refetchInterval: (query) =>
      (query.state.data?.inProgress.length ?? 0) > 0 ? 2000 : 15_000,
  });

  const { data: sync } = useQuery({
    queryKey: ["sync"],
    queryFn: () => api.get<SyncStatus>("/admin/sync"),
    refetchInterval: 30_000,
  });

  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      const messages: string[] = [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const response = await api.post<{ message: string }>(
          "/documents/upload",
          form,
          category ? { category } : undefined,
        );
        messages.push(response.message);
      }
      return messages;
    },
    onSuccess: (messages) => {
      toast(`${messages.length} file(s) queued for extraction and indexing`);
      void queryClient.invalidateQueries({ queryKey: ["ingestion"] });
    },
    onError: (caught) =>
      toast(caught instanceof Error ? caught.message : "Upload failed", "danger"),
  });

  const runSync = useMutation({
    mutationFn: (reset: boolean) =>
      api.post<{ message: string }>("/admin/sync/run", undefined, {
        reset_token: reset,
      }),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["ingestion"] });
    },
  });

  const retry = useMutation({
    mutationFn: (jobId: string) =>
      api.post<{ message: string }>(`/admin/ingestion/${jobId}/retry`),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["ingestion"] });
    },
  });

  // `MANAGE_SYNC` gates the connector, not authoring. An NCE holds
  // UPLOAD_DOCUMENTS and not MANAGE_SYNC, so `/admin/ingestion` returns 403 to
  // them -- and this screen used to answer that by replacing itself with the
  // error, which took the upload dropzone down with it. An engineer with every
  // right to add a document reached the page that adds documents and found no
  // way to add one, while the endpoint behind it would have accepted the file.
  //
  // The overview is operational reporting. It is not what the reader came for,
  // and its absence is not a reason to refuse them the thing it sits beside.
  const canSync = user?.capabilities.includes("MANAGE_SYNC") ?? false;

  if (isLoading && canSync) return <Spinner />;

  return (
    <>
      <PageHeader
        eyebrow="Knowledge base"
        title="Knowledge base ingestion"
        subtitle="Upload, OCR, chunk and embed documents into the retrieval index"
        actions={
          // Hidden rather than disabled for someone who cannot use them: both
          // of these 403, and a full sweep is a connector operation an author
          // has no business being offered.
          !canSync ? null : (
          <>
            <button
              className="btn-secondary"
              disabled={runSync.isPending}
              onClick={() => runSync.mutate(false)}
            >
              Run sync
            </button>
            <button
              className="btn-secondary"
              disabled={runSync.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    "A full sweep re-checks every item in the library. Continue?",
                  )
                )
                  runSync.mutate(true);
              }}
            >
              Full sweep
            </button>
          </>
          )
        }
      />

      <div className="flex flex-wrap items-start gap-4">
        <div className="flex min-w-0 flex-1 basis-[520px] flex-col gap-4">
          <div
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              if (event.dataTransfer.files.length) upload.mutate(event.dataTransfer.files);
            }}
            className={`rounded-[12px] border-[1.5px] border-dashed p-[38px] text-center transition-colors ${
              dragging ? "border-ink bg-accent-wash" : "border-line-strong bg-surface"
            }`}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#AD96DC"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mx-auto mb-3"
              aria-hidden
            >
              <path d="M12 16V4m0 0L8 8m4-4 4 4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
            </svg>
            <h2 className="m-0 text-title font-bold text-ink">Drop documents to index</h2>
            <p className="mx-auto mt-2 max-w-md text-base+ leading-[1.65] text-muted">
              PDF, DOCX, XLSX, PPTX and scanned images. Office formats are laid
              out before extraction so citations point at real pages, and pages
              with no text layer route through OCR first.
            </p>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              onChange={(event) =>
                event.target.files?.length && upload.mutate(event.target.files)
              }
            />
            <button
              className="btn-dark mt-4"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending ? "Uploading…" : "Browse files"}
            </button>
          </div>

          {/* Only for somebody entitled to the overview. A 403 here is the
              expected answer for an author and is not worth reporting to them;
              anything else is a real failure and the panels below will be
              empty, which needs saying rather than leaving them to wonder. */}
          {canSync && error !== null && error !== undefined && (
            <ErrorNote error={error} />
          )}

          <Card
            title="Ingestion queue"
            meta={`${data?.inProgress.length ?? 0} in progress · ${formatNumber(data?.completedCount ?? 0)} completed today`}
            padded={false}
          >
            {data?.inProgress.length === 0 ? (
              <p className="px-4 py-6 text-center text-base+ text-muted">
                Nothing is being processed.
              </p>
            ) : (
              <ul className="max-h-[420px] overflow-y-auto overscroll-contain">
                {data?.inProgress.map((job) => (
                  <QueueRow key={job.id} job={job} />
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="Completed today"
            meta={`${formatNumber(data?.completedCount ?? 0)} documents · ${formatNumber(data?.chunksToday ?? 0)} passages embedded`}
            padded={false}
          >
            {data?.completedToday.length === 0 ? (
              <p className="px-4 py-6 text-center text-base+ text-muted">
                Nothing has completed today.
              </p>
            ) : (
              <ul className="max-h-[420px] overflow-y-auto overscroll-contain">
                {data?.completedToday.map((job) => (
                  <li
                    key={job.id}
                    className="grid grid-cols-[minmax(0,1fr)_92px_110px_60px] items-center gap-3 border-b border-line-soft px-4 py-[11px] last:border-0"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-base+ font-semibold text-ink">
                        {job.filename}
                      </span>
                      <span className="block text-sm text-muted-soft">
                        {job.pageCount} pages · {formatBytes(job.size)}
                      </span>
                    </span>
                    <span className="text-sm+ text-muted">{job.chunkCount} chunks</span>
                    <span
                      className="text-sm font-extrabold uppercase tracking-[0.08em]"
                      style={{ color: job.ocrNeedsCheck ? "#B23438" : "#2F6420" }}
                    >
                      {job.ocrNeedsCheck ? "Review OCR" : "Indexed"}
                    </span>
                    <span className="text-right text-sm text-muted-soft">
                      {formatTime(job.updatedAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {(data?.failed.length ?? 0) > 0 && (
            <Card title="Failed items" meta="Retry re-queues the whole pipeline" padded={false}>
              <ul>
                {data?.failed.map((job) => (
                  <li
                    key={job.id}
                    className="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3 last:border-0"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base+ font-bold text-ink">
                        {job.filename}
                      </span>
                      <span className="block text-sm text-danger">{job.error}</span>
                      <span className="text-sm text-muted-soft">
                        {job.attempts} attempt(s) · {timeAgo(job.updatedAt)}
                      </span>
                    </span>
                    <button
                      className="btn-small"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate(job.id)}
                    >
                      Retry
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* ------------------------------------------------------ right rail */}
        <div className="flex flex-1 basis-[280px] flex-col gap-3 lg:max-w-[340px]">
          <Card title="Default metadata" collapsible storageKey="default-metadata">
            <label className="block">
              <span className="mb-1.5 block text-base+ text-muted-strong">Category</span>
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                className="select"
              >
                {CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <p className="mt-2 text-sm leading-[1.6] text-muted-soft">
              Left to infer, the category is guessed from the filename and folder
              path. The guess is shown on the document and can be corrected there.
            </p>
          </Card>

          <Card title="Document library connector" dark>
            Two-way sync with <strong className="text-pearl">
              {sync?.siteLabel || sync?.provider || "the library"}
            </strong>
            . New and edited files are picked up every 15 minutes; publish
            decisions here write back to the source library.
          </Card>

          <Card title="Extraction quality" collapsible storageKey="extraction-quality" defaultOpen={false}>
            {(data?.extractionQuality.length ?? 0) === 0 ? (
              <p className="text-base+ text-muted">No documents processed yet.</p>
            ) : (
              data?.extractionQuality.map((bar) => <Bar key={bar.label} {...bar} />)
            )}
          </Card>

          <Card title="Connector health" collapsible storageKey="connector-health" defaultOpen={false}>
            {sync ? (
              <dl className="flex flex-col gap-2">
                <Row label="Last sync" value={timeAgo(sync.lastRunAt)} />
                <Row label="Next sync" value={timeAgo(sync.nextRunAt)} />
                <Row label="Libraries watched" value={String(sync.librariesWatched)} />
                <Row
                  label="Failures (24h)"
                  value={String(sync.failures24h)}
                  tone={sync.failures24h ? "#B23438" : undefined}
                />
                <Row label="Authentication" value={sync.authMode} />
                <Row
                  label="Check-out"
                  value={sync.supportsCheckout ? "At source" : "Advisory within the DMS"}
                />
              </dl>
            ) : (
              <Spinner />
            )}
          </Card>

          <Card title="Index health" collapsible storageKey="index-health" defaultOpen={false}>
            {data?.indexHealth.map((bar) => <Bar key={bar.label} {...bar} />)}
          </Card>
        </div>
      </div>
    </>
  );
}

function QueueRow({ job }: { job: QueueItem }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_108px_140px_52px] items-center gap-3 border-b border-line-soft px-4 py-3 last:border-0">
      <span className="min-w-0">
        <span className="block truncate text-base+ font-semibold text-ink">
          {job.filename}
        </span>
        <span className="block text-sm text-muted-soft">
          {job.pageCount > 0 ? `${job.pageCount} pages · ` : ""}
          {formatBytes(job.size)}
        </span>
      </span>
      <span
        className="text-sm font-extrabold uppercase tracking-[0.08em]"
        style={{ color: STAGE_COLOUR[job.stage] ?? "#A49DA8" }}
      >
        {job.stageLabel}
      </span>
      <div className="h-[5px] w-full overflow-hidden rounded-full bg-surface-rail">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{
            width: `${job.progress}%`,
            background: STAGE_BAR[job.stage] ?? "#8DE971",
          }}
        />
      </div>
      <span className="text-right text-sm+ font-bold text-muted-soft">
        {job.progress}%
      </span>
    </li>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-base+">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd
        className="min-w-0 break-words text-right font-semibold"
        style={{ color: tone ?? "#4A444E" }}
      >
        {value}
      </dd>
    </div>
  );
}
